// What the reader sees when this application throws.
//
// It threw, on a screen somebody was using, and the page went WHITE. React unmounts
// the whole tree when a render throws and nothing here caught it — the command palette
// subscribed to a store that was not there, and the entire product disappeared, rail
// and all. No message, no way back, nothing in front of the person to report.
//
// A boundary does not fix the defect. It changes the failure from "the application
// vanished" into "this part of it stopped, here is the rest of it, and here is what to
// tell somebody" — which is the difference between an outage and a bug.
//
// # It wraps the CONTENT, not the shell
//
// Placed inside the frame rather than around it, so a screen that throws leaves the
// navigation standing and the reader can go somewhere else. A boundary at the root
// would take the rail down with the screen, which is the failure it exists to prevent.
//
// # It does NOT retry by itself
//
// A boundary that re-renders on a timer re-throws on a timer. The reader gets a
// button, because they are the one who knows whether anything changed.

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  readonly children: ReactNode;
  /** Rendered instead of the children, given what went wrong. */
  readonly fallback: (error: Error, retry: () => void) => ReactNode;
}

interface State {
  readonly error: Error | null;
}

export class Boundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The console is where a developer looks and the only reporting surface this
    // product has today. Left deliberately rather than silently swallowed: a boundary
    // that hides the stack turns a crash into a mystery.
    console.error("riksu: a screen threw and was caught by the boundary", error, info);
  }

  render() {
    const { error } = this.state;
    if (error) {
      return this.props.fallback(error, () => this.setState({ error: null }));
    }
    return this.props.children;
  }
}
